import classNames from "classnames";
import React from "react";
import { useLocation } from "react-router-dom";
import { useSidebar } from "./SidebarContext";
import { isFeatureEnabled } from "@/config/featureFlags";
import NavLink from "./NavLink";
import NavDropdown from "./NavDropdown";

interface NavItemProps {
  item: {
    name?: string;
    title?: string;
    divider?: boolean;
    label?: string | { variant?: string; class?: string };
    url?: string;
    modernizedUrl?: string;
    icon?: string;
    badge?: {
      variant: string;
      text: string;
    };
    children?: any[];
    subMenu?: any[];
    toggleState?: string;
    auth?: string | string[];
    feature?: string;
    featureFlag?: string;
    class?: string;
    variant?: string;
    wrapper?: {
      element: string;
      attributes: any;
    };
  };
  itemKey: string | number;
  parentKey?: string;
}

const NavItem: React.FC<NavItemProps> = ({ item, itemKey, parentKey = "" }) => {
  const { compactStyle, auth } = useSidebar();
  const routerLocation = useLocation();
  const location = routerLocation;

  const shouldShowMenuItem = (subItem: any): boolean => {
    // No auth required - show item
    if (subItem.auth == null || subItem.auth === undefined) {
      return true;
    }

    // Special case: Internal (company id 2 only)
    if (subItem.auth === "Internal") {
      return (auth?.user as any)?.company?.id === 2;
    }

    // Auth required - need user
    if (!auth?.user) return false;

    const userPermissions = auth.user?.userPermissions ?? [];
    const hasPermission = (perm: string) =>
      userPermissions.some(
        (up: any) => (up?.permission?.name ?? "") === perm
      );

    if (Array.isArray(subItem.auth)) {
      if (!subItem.auth.every((perm: string) => hasPermission(perm))) {
        return false;
      }
    } else {
      if (!hasPermission(subItem.auth)) {
        return false;
      }
    }

    // Check feature flag (support both 'feature' and 'featureFlag')
    if (subItem.feature || subItem.featureFlag) {
      return isFeatureEnabled(subItem.feature || subItem.featureFlag, auth?.user?.id?.toString());
    }

    return true;
  };

  if (!shouldShowMenuItem(item)) {
    return null;
  }

  const wrapper = (item: any) => {
    return item.wrapper && item.wrapper.element
      ? React.createElement(
          item.wrapper.element,
          item.wrapper.attributes,
          item.name,
        )
      : item.name;
  };

  // Handle different item types
  if (item.title) {
    const classes = classNames("nav-title", item.class);
    return (
      <li key={itemKey} className={classes}>
        {wrapper(item)}
      </li>
    );
  }

  if (item.divider) {
    const classes = classNames("divider", item.class);
    return <li key={itemKey} className={classes}></li>;
  }

  if (item.label) {
    // Handle label - item.label can be string or object
    const labelText = typeof item.label === "string" ? item.label : (item.label as any).text || item.name || "";
    const labelObj = typeof item.label === "object" ? item.label : null;
    const classes = {
      item: classNames("hidden-cn", item.class),
      link: classNames(
        "nav-label",
        item.class ? item.class : "",
        labelObj?.variant ? `text-${labelObj.variant}` : "",
        labelObj?.class ? labelObj.class : ""
      ),
      icon: classNames(
        !item.icon ? "fas fa-circle" : item.icon,
      ),
    };
    // Handle label as a special type of link
    const url = item.modernizedUrl ?? item.url ?? "";
    const currentPath =
      location?.pathname ||
      window.location.hash.replace("#", "").split("?")[0] ||
      "";
    const legacyUrl = item.url ?? "";
    const isActive =
      currentPath === url ||
      currentPath.startsWith(url + "/") ||
      currentPath === legacyUrl ||
      currentPath.startsWith(legacyUrl + "/");
    return (
      <NavLink
        item={{ ...item, name: labelText }}
        itemKey={itemKey}
        isActive={isActive}
        shouldShow={true}
      />
    );
  }

  // Handle dropdown
  if (item.children || item.subMenu) {
    const children = (item.children || item.subMenu || []).filter((child: any) =>
      shouldShowMenuItem(child)
    );
    if (!item.name) {
      // Dropdown must have a name
      return null;
    }
    return (
      <NavDropdown
        item={{ ...item, name: item.name }}
        itemKey={itemKey}
        shouldShow={true}
        children={children}
      />
    );
  }

  // Handle regular link
  if (!item.name) {
    return null;
  }
  const url = item.modernizedUrl ?? item.url ?? "";
  const legacyUrl = item.url ?? "";
  const currentPath =
    location?.pathname ||
    window.location.hash.replace("#", "").split("?")[0] ||
    "";
  const isActive =
    currentPath === url ||
    currentPath.startsWith(url + "/") ||
    currentPath === legacyUrl ||
    currentPath.startsWith(legacyUrl + "/");

  return (
    <NavLink
      item={{ ...item, name: item.name }}
      itemKey={itemKey}
      isActive={isActive}
      shouldShow={true}
      isDropdownItem={!!parentKey}
    />
  );
};

export default NavItem;
